import { useHandler } from "@chatbotgang/etude/react/useHandler";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import type { SxProps } from "@mui/material/styles";
import type { muiTheme } from "@polifonia/theme";
import { theme } from "@polifonia/theme";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button";

const styles = {
  root: {
    alignItems: "center",
    gap: "16px",
  },
  // Wide 3:1 (W:H) cover preview — hero images are banners, not square avatars.
  preview: {
    width: "240px",
    aspectRatio: "3 / 1",
    flexShrink: 0,
    borderRadius: "8px",
    overflow: "hidden",
    backgroundColor: theme.colors.neutral030,
  },
  previewImage: {
    display: "block",
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

namespace CoverImageUpload {
  export interface Props {
    onChange: (file: File) => void;
    onClear?: VoidFunction;
    inputRef: React.RefObject<HTMLInputElement | null>;
    disable?: boolean;
    value?: string;
    sx?: SxProps<typeof muiTheme>;
  }
}

export const CoverImageUpload: React.FC<CoverImageUpload.Props> =
  function CoverImageUpload({
    value,
    onChange,
    inputRef,
    onClear,
    disable = false,
    sx,
  }) {
    const { t } = useTranslation();

    const handleOnChange = useHandler(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;
        onChange(file);
      },
    );

    const handleButtonClick = useHandler(() => {
      inputRef.current?.click();
    });

    return (
      <Stack direction="row" sx={{ ...styles.root, ...sx }}>
        <Box sx={styles.preview}>
          {value ? (
            <Box component="img" src={value} alt="" sx={styles.previewImage} />
          ) : null}
        </Box>
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/jpg,image/png"
          onChange={handleOnChange}
          disabled={disable}
          hidden
        />
        <Stack direction="row" gap="8px">
          {!onClear ? null : (
            <Button variant="plain" onClick={onClear}>
              {t("resource.attachment.resetImage")}
            </Button>
          )}
          <Button variant="plain" loading={disable} onClick={handleButtonClick}>
            {t("resource.attachment.uploadImage")}
          </Button>
        </Stack>
      </Stack>
    );
  };
